<script setup lang="ts">
/**
 * MemberRow — renders one board member row in BoardMembersView.
 *
 * Used in both the desktop table and as a mobile card stack.
 * Role badge colors are pixel-locked to spec §2.4 and must not be changed.
 */
import BaseBadge from '@/components/ui/BaseBadge.vue';
import type { MemberListItemDto, MemberInvitationDto } from '@/stores/boardMembers';
import type { BoardRole } from '@/api/boardTypes';

interface Props {
  /** A live member row */
  member?: MemberListItemDto;
  /** A pending invitation row (Pending invites tab) */
  invitation?: MemberInvitationDto;
}

defineProps<Props>();

const emit = defineEmits<{
  edit: [id: string];
  resend: [invitationId: string];
  revoke: [invitationId: string];
}>();

// ── Role badge ────────────────────────────────────────────────────────────────

const ROLE_LABEL: Record<BoardRole, string> = {
  President: 'President',
  Treasurer: 'Treasurer',
  Secretary: 'Secretary',
  PropertyManager: 'Property Manager',
  MaintenanceChair: 'Maintenance Chair',
  BoardMember: 'Board Member',
};

// Tailwind classes per spec §2.4 — do NOT change these.
const ROLE_CLASS: Record<BoardRole, string> = {
  President:        'bg-navy-100 text-navy-700 border-navy-100',
  Treasurer:        'bg-teal-100 text-teal-700 border-teal-100',
  Secretary:        'bg-violet-100 text-violet-600 border-violet-100',
  PropertyManager:  'bg-[--color-pm-100] text-[--color-pm-600] border-[--color-pm-100]',
  MaintenanceChair: 'bg-orange-100 text-orange-600 border-orange-100',
  BoardMember:      'bg-canvas-soft text-fg-soft border-border',
};

function roleBadgeClass(role: BoardRole): string {
  return `inline-flex items-center gap-1.5 px-2 h-[22px] text-caption font-semibold rounded-pill border whitespace-nowrap ${ROLE_CLASS[role]}`;
}

// ── Notification prefs ────────────────────────────────────────────────────────

function hasSms(m: MemberListItemDto): boolean {
  if (!m.notificationMethodsJson) return false;
  try {
    return (JSON.parse(m.notificationMethodsJson) as string[]).includes('sms');
  } catch { return false; }
}
function hasEmail(m: MemberListItemDto): boolean {
  if (!m.notificationMethodsJson) return false;
  try {
    return (JSON.parse(m.notificationMethodsJson) as string[]).includes('email');
  } catch { return false; }
}
function hasPush(m: MemberListItemDto): boolean {
  if (!m.notificationMethodsJson) return false;
  try {
    return (JSON.parse(m.notificationMethodsJson) as string[]).includes('push');
  } catch { return false; }
}

// ── Alert categories ──────────────────────────────────────────────────────────

function alertCategories(m: MemberListItemDto): string[] | null {
  if (!m.alertCategoriesJson) return null;
  try {
    return JSON.parse(m.alertCategoriesJson) as string[];
  } catch { return null; }
}

// ── Relative time ─────────────────────────────────────────────────────────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Avatar initials ───────────────────────────────────────────────────────────

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}
</script>

<template>
  <!-- Live member row -->
  <tr v-if="member" data-testid="member-row" class="hover:bg-canvas-soft transition-colors">
    <!-- Member name + email -->
    <td class="px-4 py-3">
      <div class="flex items-center gap-2.5">
        <div
          class="w-8 h-8 rounded-pill bg-navy-100 text-navy-700 flex items-center justify-center font-semibold text-caption flex-shrink-0"
        >
          {{ initials(member.name) }}
        </div>
        <div class="min-w-0">
          <div class="text-body font-semibold text-fg leading-tight truncate">
            {{ member.name }}
            <span
              v-if="member.isExternal"
              class="ml-1.5 inline text-caption font-normal text-muted"
            >external</span>
          </div>
          <div class="text-caption text-muted font-mono tabular-nums truncate">
            {{ member.email }}
          </div>
        </div>
      </div>
    </td>

    <!-- Role badge -->
    <td class="px-4 py-3">
      <span :class="roleBadgeClass(member.role)">
        <span
          class="w-1.5 h-1.5 rounded-pill bg-current"
          aria-hidden="true"
        />
        {{ ROLE_LABEL[member.role] }}
      </span>
    </td>

    <!-- Notification pref icons -->
    <td class="px-4 py-3">
      <div class="flex items-center gap-2">
        <!-- SMS -->
        <span
          :class="[
            'w-7 h-7 rounded-md grid place-items-center',
            hasSms(member) ? 'bg-green-100 text-green-700' : 'bg-canvas-soft text-muted-2',
          ]"
          :title="hasSms(member) ? 'SMS enabled' : 'SMS disabled'"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
            <path d="M22 11v3a4 4 0 0 1-4 4H8l-5 4V6a4 4 0 0 1 4-4h7" />
          </svg>
        </span>
        <!-- Email -->
        <span
          :class="[
            'w-7 h-7 rounded-md grid place-items-center',
            hasEmail(member) ? 'bg-green-100 text-green-700' : 'bg-canvas-soft text-muted-2',
          ]"
          :title="hasEmail(member) ? 'Email enabled' : 'Email disabled'"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        </span>
        <!-- Push -->
        <span
          :class="[
            'w-7 h-7 rounded-md grid place-items-center',
            hasPush(member) ? 'bg-green-100 text-green-700' : 'bg-canvas-soft text-muted-2',
          ]"
          :title="hasPush(member) ? 'Push enabled' : 'Push disabled'"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
            <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          </svg>
        </span>
      </div>
    </td>

    <!-- Alert categories -->
    <td class="px-4 py-3">
      <template v-if="alertCategories(member)">
        <div class="flex flex-wrap gap-1">
          <span
            v-for="cat in alertCategories(member)!.slice(0, 3)"
            :key="cat"
            class="inline-flex items-center px-2 h-5 text-caption bg-canvas-soft text-fg-soft border border-border rounded-pill"
          >{{ cat }}</span>
          <span
            v-if="alertCategories(member)!.length > 3"
            class="inline-flex items-center px-2 h-5 text-caption text-muted"
          >+{{ alertCategories(member)!.length - 3 }}</span>
        </div>
      </template>
      <span v-else class="text-caption text-muted">All categories</span>
    </td>

    <!-- Last active -->
    <td class="px-4 py-3">
      <span class="font-mono tabular-nums text-caption text-muted">
        {{ relativeTime(member.lastActiveAt) }}
      </span>
    </td>

    <!-- Actions -->
    <td class="px-4 py-3 text-right">
      <button
        class="h-8 px-2.5 text-caption text-muted hover:text-fg border border-border rounded-md hover:bg-canvas-soft transition-colors"
        @click="emit('edit', member.id)"
      >
        Edit
      </button>
    </td>
  </tr>

  <!-- Pending invitation row -->
  <tr v-else-if="invitation" data-testid="member-row" class="bg-canvas-soft">
    <!-- Email (no name yet) -->
    <td class="px-4 py-3">
      <div class="flex items-center gap-2.5">
        <div
          class="w-8 h-8 rounded-pill bg-border text-muted flex items-center justify-center flex-shrink-0"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        </div>
        <div class="min-w-0">
          <div class="text-body text-muted truncate">{{ invitation.email }}</div>
          <div class="text-caption text-muted-2">
            Invited ·
            <span class="font-mono tabular-nums">
              expires {{ new Date(invitation.expiresAt).toLocaleDateString() }}
            </span>
          </div>
        </div>
      </div>
    </td>

    <!-- Role badge -->
    <td class="px-4 py-3">
      <span :class="roleBadgeClass(invitation.role)">
        <span class="w-1.5 h-1.5 rounded-pill bg-current" aria-hidden="true" />
        {{ ROLE_LABEL[invitation.role] }}
      </span>
    </td>

    <!-- Status -->
    <td class="px-4 py-3">
      <BaseBadge variant="pending" :no-dot="false">Pending</BaseBadge>
    </td>

    <!-- Empty cells -->
    <td class="px-4 py-3"><span class="text-muted text-caption">—</span></td>
    <td class="px-4 py-3"><span class="text-muted text-caption">—</span></td>

    <!-- Resend / Revoke -->
    <td class="px-4 py-3 text-right">
      <div class="flex justify-end gap-1.5">
        <button
          class="h-8 px-2.5 text-caption text-muted hover:text-fg border border-border rounded-md hover:bg-canvas-soft transition-colors"
          data-testid="resend-invite-btn"
          @click="emit('resend', invitation.id)"
        >
          Resend
        </button>
        <button
          class="h-8 px-2.5 text-caption text-red-700 hover:bg-red-50 border border-border rounded-md transition-colors"
          data-testid="revoke-invite-btn"
          @click="emit('revoke', invitation.id)"
        >
          Revoke
        </button>
      </div>
    </td>
  </tr>
</template>
